import React from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget, axiospost } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import is_null from "../../helpers/is_null";
import ErrorResponse from "../../components/ErrorResponse";
import "./PrintResult.css"; // New CSS file
import PrintHeader from "../../Print/PrintHeader";
import ReceiptHeader from "../PaymentReceipts/ReceiptHeader";

const PrintResult = () => {
  const [progress, setProgress] = React.useState(null);
  const { id } = useParams();
  const queryClient = useQueryClient();

  // Data fetching logic
  const cachedData = queryClient.getQueryData(["VerticalTimeline"]);
  const { data, isLoading } = useQuery({
    queryKey: ["PrintResultVerticalTimeline",id],
    queryFn: async () => {
      const response = await axiosget("ms_progression_profile/courseform");
      console.log(response.data)
      return response?.data;
    },
    enabled: !cachedData,
  });

  const {
    data: register,
    isLoading: loadRegister,
    error: errRegister,
  } = useQuery({
    queryKey: ["courseRegistrationPrint",id],
    queryFn: async () => {
      const response = await axiospost("ms_course_result", progress);
      console.log(response.data)
      return response?.data;
    },
    enabled: !!progress,
  });

  const timelineData = cachedData || data;
  const sortedData = timelineData?.sort((a, b) => a.step - b.step);

  React.useEffect(() => {
    if (id && sortedData) {
      const stepObject = sortedData.find((item) => item._id === id);
      setProgress(stepObject);
    }
  }, [id, sortedData]);

  // Calculate academic statistics
  const calculateStats = () => {
    if (!register?.result) return null;

    const totalCredits = register.result.reduce((sum, course) => sum + (course.credit || 0), 0);
    const totalWeightedPoints = register.result.reduce((sum, course) => sum + (course.point * course.credit || 0), 0);
    const gpa = totalCredits > 0 ? (totalWeightedPoints / totalCredits).toFixed(2) : 0;
    const carryovers = register.result
      .filter((course) => course.point === 0)
      .map((course) => course.code)
      .join(", ");

    return {
      totalCredits,
      totalWeightedPoints,
      gpa,
      carryovers,
    };
  };

  const stats = calculateStats();

  if (is_null(id)) {
    window.close();
    return null;
  }

  // const formatDate = () => {
  //   return new Date().toLocaleDateString("en-US", {
  //     year: "numeric",
  //     month: "long",
  //     day: "numeric",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //   });
  // };

  return (
    <div className="receipt-container">
      {isLoading || loadRegister ? <Loader /> : null}
      {errRegister && <ErrorResponse type="danger" message={`Error: ${errRegister.message}`} />}
      {register?.type !== undefined && <ErrorResponse type="danger" message={register?.message} />}

      {register && register?.type === undefined && (
        <>
          {register?.result?.length > 0 ? (
            <div className="result-document">
              <div className="print-header1-section">
              <ReceiptHeader />
            </div>
              <div className="receipt">
                {/* Receipt Header */}
                <div className="receipt-header">
                  <h2>OFFICIAL STUDENT RESULT</h2>
                  <div className="receipt-meta">
                    <span>Date Generated: {new Date().toLocaleDateString()}</span>
                  </div>
                </div>
                {/* Student Profile Section */}
                <div className="receipt-section">
                  <div className="student-info" style={{ padding: "15px" }}>
                    <div className="info-column">
                      <div className="info-row">
                        <span className="info-label">Reg. Number:</span>
                        <span className="info-value">{register?.student?.studentid}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Full Name:</span>
                        <span className="info-value">
                          {register?.student?.firstname} {register?.student?.middlename} {register?.student?.lastname}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">School:</span>
                        <span className="info-value">{register?.student?.programme?.department?.college?.name}</span>
                      </div>
                    </div>
                    <div className="info-column">
                      <div className="info-row">
                        <span className="info-label">Department:</span>
                        <span className="info-value">{register?.student?.programme?.department?.name}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Programme:</span>
                        <span className="info-value">{register?.student?.programme?.name}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Session:</span>
                        <span className="info-value">{register?.session?.name}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="results-table-container">
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Code</th>
                        <th>Course Title</th>
                        <th>CA Score</th>
                        <th>Exam Score</th>
                        <th>Total</th>
                        <th>Credit</th>
                        <th>Point</th>
                        <th>Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {register.result.map((course, index) => (
                        <tr key={`course-${index}`}>
                          <td>{index + 1}</td>
                          <td>{course?.course.code}</td>
                          <td>{course?.course.name}</td>
                          <td>{course.cascore}</td>
                          <td>{course.examscore}</td>
                          <td>{course.total}</td>
                          <td>{course.credit}</td>
                          <td>{course.point}</td>
                          <td>{course.grade}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {stats?.carryovers && (
                  <div className="carryover-notice">
                    <strong>Carryover Courses:</strong> {stats.carryovers}
                  </div>
                )}

                <div className="performance-summary">
                  <div className="summary-item">
                    <div className="summary-value">{stats?.totalCredits || 0}</div>
                    <div className="summary-label">Total Credits Registered</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats?.totalWeightedPoints || 0}</div>
                    <div className="summary-label">Total Weighted Points</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats?.gpa || 0}</div>
                    <div className="summary-label">Grade Point Average</div>
                  </div>
                </div>

                <div className="signature-section">
                  <div className="signature-line"></div>
                  <div className="signature-label">Head of Department's Signature & Stamp</div>
                  <div className="signature-date">Date: ___________________</div>
                </div>

                <div className="document-notice">
                  <i>
                    Tampering with any score/grade on this document renders it invalid. Students are advised to keep this statement of result
                    unchanged.
                  </i>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-results-container">
              <PrintHeader />
              <div className="no-results-alert">
                <strong>Notice:</strong> Results are not yet available for the selected level & semester.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PrintResult;
